import { Avatar } from "@/components/ui/avatar";
import { getInitials } from "@/utils/string";

type UserIdentityProps = {
  name?: string | null;
  email?: string | null;
  meta?: string;
  showAvatar?: boolean;
};

/**
 * A user's name with their email underneath, as shown on the Users, Audit Log
 * and Access Control pages (MDRS-496). A missing or blank name promotes the
 * email to the name line rather than showing it twice; neither renders "—".
 * Spans only, so it can sit inside a button.
 */
export const UserIdentity = ({
  name,
  email,
  meta,
  showAvatar = true,
}: UserIdentityProps) => {
  const trimmedName = name?.trim() || null;
  const trimmedEmail = email?.trim() || null;
  const primary = trimmedName ?? trimmedEmail;

  if (!primary) {
    return <span className="text-xs">—</span>;
  }

  const secondary = [trimmedName ? trimmedEmail : null, meta]
    .filter(Boolean)
    .join(" · ");

  return (
    <span className="flex min-w-0 items-center gap-3">
      {showAvatar && (
        <Avatar
          size="sm"
          aria-hidden="true"
          fallback={getInitials(trimmedName ?? primary[0])}
          fallbackClassName="bg-primary-50 text-primary font-bold text-[11px] border-transparent"
        />
      )}
      <span className="block min-w-0">
        <span className="block truncate font-head text-[13px] font-semibold text-foreground">
          {primary}
        </span>
        {secondary && (
          <span className="block truncate text-xs text-foreground-muted">
            {secondary}
          </span>
        )}
      </span>
    </span>
  );
};
